import { MouseEvent, useEffect, useRef, useState } from "react";
import { OverlayPanel } from "primereact/overlaypanel";
import { Row, Spacer } from "../../components/Layout/Layout";
import useTranslations from "../../core/i18n/useTranslations";
import { TransmitterState } from "../../core/hooks/filters/useTransmitterFilter";
import Button from "../../ui-lib/components/Button/Button";
import TextInput from "../../ui-lib/components/Inputs/TextInput";
import { useFocusTrap } from "../../ui-lib/utils/useFocusTrap";
import { ClearFilter } from "../../ui-lib/components/Tables/CleatFilters";
import styles from "./Transmitters.module.css";

interface TransmitterFiltersProps {
  transmitterFilter: TransmitterState;
  setTransmitterFilter: (args: Partial<TransmitterState>) => void;
  resetPageFilter: () => void;
}

type Draft = Pick<TransmitterState, "version" | "model" | "manufacturer">;

const toDraft = (filter: TransmitterState): Draft => ({
  version: filter.version,
  model: filter.model,
  manufacturer: filter.manufacturer,
});

export const TransmitterFilters = ({
  transmitterFilter,
  setTransmitterFilter,
  resetPageFilter,
}: TransmitterFiltersProps) => {
  const t = useTranslations();
  const overlayRef = useRef<OverlayPanel>(null);
  const [draft, setDraft] = useState<Draft>(toDraft(transmitterFilter));

  const {
    containerRef: popupBodyRef,
    onKeyDown: handleKeyDown,
    onShow: handlePopupShow,
    onHide: handlePopupHide,
  } = useFocusTrap(true);

  useEffect(() => {
    setDraft(toDraft(transmitterFilter));
  }, [transmitterFilter]);

  const resetDraft = () =>
    setDraft({ version: "", model: "", manufacturer: "" });

  const closeModal = () => overlayRef.current?.hide();

  const activeCount = [
    transmitterFilter.version,
    transmitterFilter.model,
    transmitterFilter.manufacturer,
  ].filter(Boolean).length;

  const handleTriggerClick = (e: MouseEvent<HTMLButtonElement>) => {
    overlayRef.current?.toggle(e);
  };

  const handleApply = () => {
    setTransmitterFilter(draft);
    resetPageFilter();
    closeModal();
  };

  const handleCancel = () => {
    setDraft(toDraft(transmitterFilter));
    closeModal();
  };

  const handleClear = () => {
    setTransmitterFilter({ model: "", manufacturer: "", version: "" });
    resetDraft();
    resetPageFilter();
    closeModal();
  };

  const appliedFilterText = (count: number) =>
    `${count} ${count > 1 ? t("Common:custom_filters_many") : t("Common:custom_filters_single")}`;

  return (
    <>
      <Button
        image="filter"
        variant={activeCount > 0 ? "active" : "secondary"}
        text={
          activeCount > 0
            ? appliedFilterText(activeCount)
            : t("Common:custom_filters_more")
        }
        className={styles.filterButton}
        onClick={handleTriggerClick}
        dataCy="transmitter_filters_btn"
      />

      <OverlayPanel
        ref={overlayRef}
        onShow={handlePopupShow}
        onHide={handlePopupHide}
        onKeyDown={handleKeyDown}
        className={styles.filterModal}
        pt={{
          root: {
            role: "dialog",
            "aria-label": t("Common:custom_filters_more"),
          },
        }}
      >
        <div ref={popupBodyRef}>
          <Row>
            <TextInput
              label={t("Transmitters:model")}
              value={draft.model}
              placeholder={t("Transmitters:model_placeholder")}
              onChange={(_, value) =>
                setDraft((prev) => ({ ...prev, model: value }))
              }
            />
            <Spacer size={16} />
            <TextInput
              label={t("Transmitters:manufacturer")}
              value={draft.manufacturer}
              placeholder={t("Transmitters:manufacturer_placeholder")}
              onChange={(_, value) =>
                setDraft((prev) => ({ ...prev, manufacturer: value }))
              }
            />
          </Row>
          <Spacer size={16} />
          <TextInput
            label={t("Transmitters:version")}
            value={draft.version}
            placeholder={t("Transmitters:version_placeholder")}
            className="w-100"
            onChange={(_, value) =>
              setDraft((prev) => ({ ...prev, version: value }))
            }
          />
          <Spacer size={16} />
          <Row type="space">
            <Row type="left">
              <Button
                variant="primary"
                text={t("Common:custom_filters_apply")}
                onClick={handleApply}
                dataCy="apply_transmitter_filters_btn"
              />
              <Spacer size={8} />
              <Button
                variant="secondary"
                text={t("Common:cancel")}
                onClick={handleCancel}
                dataCy="cancel_transmitter_filters_btn"
              />
            </Row>
            <ClearFilter
              text={t("Common:labels_clear_filters")}
              filterToWatch={{
                version: transmitterFilter.version,
                model: transmitterFilter.model,
                manufacturer: transmitterFilter.manufacturer,
              }}
              onClearClick={handleClear}
            />
          </Row>
        </div>
      </OverlayPanel>
    </>
  );
};
